import { FC, useRef, useState } from 'react';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { BacksolveSourceRow } from 'api/models/BacksolveSourceRow';
import { smallCurrencyFormat, weightingPercentFormat } from 'common/formats/formats';
import { Cells, GridDateProps } from 'common/types/scalarSpreadsheet';
import { GridDate, GridDateEditor, GridDateValueViewer } from 'components/FeaturedSpreadsheet/components';
import {
  BOLD_DIVISION_ROW_HEADER_CONFIG,
  TABLE_HEADER_CONFIG,
} from 'pages/Valuations/approaches/backsolveApproach/constants';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { normalizeDate } from 'utilities/dateUtils/dateUtils';
import {
  COLUMN_HEADERS,
  NUM_COLUMNS,
  SOURCE_ROWS_TABLE_DATE_INDEX,
  SOURCE_ROWS_TABLE_HEADER_ROW,
  SOURCE_ROWS_TABLE_OFFSET,
  SOURCE_ROWS_TABLE_PRICE_COLUMN,
  SOURCE_ROWS_TABLE_PRICE_INDEX,
  SOURCE_ROWS_TABLE_SIZE_INDEX,
  SOURCE_ROWS_TABLE_SOURCE_INDEX,
  SOURCE_ROWS_TABLE_TARGET_PRICE_TITLE,
  SOURCE_ROWS_TABLE_TITLE_INDEX,
  SOURCE_ROWS_TABLE_WEIGHTING_COLUMN,
  SOURCE_ROWS_TABLE_WEIGHTING_INDEX,
} from '../constants';
import { getSourceRowCellKey } from '../getSourceRowKey';
import {
  SOURCE_ROW_DATE,
  SOURCE_ROW_PRICE,
  SOURCE_ROW_SIZE,
  SOURCE_ROW_SOURCE,
  SOURCE_ROW_TITLE,
  SOURCE_ROW_WEIGHTING,
  SOURCE_ROW_ZX_INDEX_RECORD_ID,
  SOURCE_TOTALS_ROW,
  SOURCE_TOTALS_ROW_TARGET,
} from '../sheetAliases';
import { CustomParserParams } from './types';

const TitleCellContent = ({ text }: { text: string }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  const handleMouseEnter = () => {
    const el = spanRef.current;
    if (el && el.scrollWidth > el.clientWidth) setOpen(true);
  };

  return (
    <Tooltip title={text} open={open} onClose={() => setOpen(false)} placement="top">
      <span
        ref={spanRef}
        onMouseEnter={handleMouseEnter}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
      >
        {text}
      </span>
    </Tooltip>
  );
};

const getSizeDisplayValue = (row: BacksolveSourceRow): string => {
  if (row.size != null && row.size !== '') {
    return String(row.size);
  }
  if (row.source_record_id === SOURCE_ROW_ZX_INDEX_RECORD_ID) {
    return '-';
  }
  return '';
};

// Preserve empty as '' so isRequired fires and FastValueViewer shows ENTER DATA
// instead of a formatted zero ($0.00 / 0.0%).
const getNumericCellValue = (raw: string | null | undefined): number | '' => {
  if (raw == null || raw === '') {
    return '';
  }
  return Number(raw);
};

const getHeaderClassName = (colIdx: number): string => {
  const base = TABLE_HEADER_CONFIG.className;
  if (
    colIdx === SOURCE_ROWS_TABLE_DATE_INDEX ||
    colIdx === SOURCE_ROWS_TABLE_SOURCE_INDEX ||
    colIdx === SOURCE_ROWS_TABLE_SIZE_INDEX ||
    colIdx === SOURCE_ROWS_TABLE_PRICE_INDEX ||
    colIdx === SOURCE_ROWS_TABLE_WEIGHTING_INDEX
  ) {
    return `${base} text-right`;
  }
  return base;
};

const customParser = ({ tableData }: CustomParserParams): Record<string, object> => {
  const { sourceRows, isDisabled, tableTitle = '', companyOverviewUrl } = tableData;
  const alphabet = alphabetGenerator([], NUM_COLUMNS);
  const cells: Cells = {};

  const makeKey = (colIdx: number, rowNumber: number) => `${alphabet[colIdx]}${rowNumber}`;

  // Row 1: combined header — table title in col A, column labels in cols B–F
  COLUMN_HEADERS.forEach((header, colIdx) => {
    const key = makeKey(colIdx, SOURCE_ROWS_TABLE_HEADER_ROW);
    cells[key] = {
      ...TABLE_HEADER_CONFIG,
      key,
      className: getHeaderClassName(colIdx),
      value: header,
      expr: header,
      readOnly: true,
      gridType: 'string',
      columnLegend: alphabet[colIdx],
      rowNumber: SOURCE_ROWS_TABLE_HEADER_ROW,
    };
  });

  // Rows 3..N+2: one per source row
  sourceRows.forEach((row, rowIdx) => {
    const rowNumber = rowIdx + SOURCE_ROWS_TABLE_OFFSET;
    const isAutoImported = row.source_record_id !== null;
    const isManualRowRequired = !isAutoImported && !isDisabled;

    // A: Title — read-only rows use a tooltip viewer; editable rows rely on the default viewer so edits persist on commit.
    const titleText = row.title ?? '';
    const titleCell: Record<string, unknown> = {
      key: makeKey(SOURCE_ROWS_TABLE_TITLE_INDEX, rowNumber),
      alias: getSourceRowCellKey(SOURCE_ROW_TITLE, row, rowIdx),
      value: titleText,
      expr: titleText,
      readOnly: isAutoImported || isDisabled,
      isRequired: isManualRowRequired,
      gridType: 'string',
      columnLegend: alphabet[SOURCE_ROWS_TABLE_TITLE_INDEX],
      rowNumber,
    };
    if (isAutoImported || isDisabled) {
      titleCell.valueViewer = () => <TitleCellContent text={titleText} />;
    }
    cells[makeKey(SOURCE_ROWS_TABLE_TITLE_INDEX, rowNumber)] = titleCell;

    // B: Date — manual rows use the grid date editor/viewer; auto-imported rows stay read-only text.
    const parsedDate = normalizeDate(row.date);
    const formattedDate = parsedDate
      ? `${String(parsedDate.getUTCMonth() + 1).padStart(2, '0')}/${String(parsedDate.getUTCDate()).padStart(2, '0')}/${parsedDate.getUTCFullYear()}`
      : row.date ?? '';
    const dateCell: Record<string, unknown> = {
      key: makeKey(SOURCE_ROWS_TABLE_DATE_INDEX, rowNumber),
      alias: getSourceRowCellKey(SOURCE_ROW_DATE, row, rowIdx),
      value: formattedDate,
      expr: formattedDate,
      readOnly: isAutoImported || isDisabled,
      isRequired: isManualRowRequired,
      gridType: isAutoImported ? 'string' : 'date',
      dbType: 'string',
      columnLegend: alphabet[SOURCE_ROWS_TABLE_DATE_INDEX],
      rowNumber,
    };
    if (!isAutoImported) {
      dateCell.gridDateComponent = GridDate as FC<GridDateProps>;
      dateCell.dataEditor = (props: any) => <GridDateEditor {...props} />;
      dateCell.valueViewer = (props: any) => <GridDateValueViewer {...props} />;
    }
    cells[makeKey(SOURCE_ROWS_TABLE_DATE_INDEX, rowNumber)] = dateCell;

    // C: Source — auto-imported rows render as a link to the Company Overview page
    const sourceValue = row.source ?? '';
    const sourceCell: Record<string, unknown> = {
      key: makeKey(SOURCE_ROWS_TABLE_SOURCE_INDEX, rowNumber),
      alias: getSourceRowCellKey(SOURCE_ROW_SOURCE, row, rowIdx),
      value: sourceValue,
      expr: sourceValue,
      readOnly: isAutoImported || isDisabled,
      isRequired: isManualRowRequired,
      gridType: 'string',
      columnLegend: alphabet[SOURCE_ROWS_TABLE_SOURCE_INDEX],
      rowNumber,
    };
    if (isAutoImported && companyOverviewUrl) {
      const url = companyOverviewUrl;
      const label = sourceValue;
      sourceCell.valueViewer = () => (
        <a href={url} target="_blank" rel="noopener noreferrer" className="source-row-link">
          {label}
          <OpenInNewIcon sx={{ fontSize: '0.7rem !important', verticalAlign: 'middle', ml: 0.25 }} />
        </a>
      );
    }
    cells[makeKey(SOURCE_ROWS_TABLE_SOURCE_INDEX, rowNumber)] = sourceCell;

    // D: Size — '-' placeholder is only for the ZX Index Price row; manual rows start empty.
    const sizeValue = getSizeDisplayValue(row);
    cells[makeKey(SOURCE_ROWS_TABLE_SIZE_INDEX, rowNumber)] = {
      key: makeKey(SOURCE_ROWS_TABLE_SIZE_INDEX, rowNumber),
      alias: getSourceRowCellKey(SOURCE_ROW_SIZE, row, rowIdx),
      value: sizeValue,
      expr: sizeValue,
      readOnly: isAutoImported || isDisabled,
      isRequired: isManualRowRequired,
      gridType: 'string',
      columnLegend: alphabet[SOURCE_ROWS_TABLE_SIZE_INDEX],
      rowNumber,
    };

    // E: Price Per Share
    const priceValue = getNumericCellValue(row.price_per_share);
    cells[makeKey(SOURCE_ROWS_TABLE_PRICE_INDEX, rowNumber)] = {
      key: makeKey(SOURCE_ROWS_TABLE_PRICE_INDEX, rowNumber),
      alias: getSourceRowCellKey(SOURCE_ROW_PRICE, row, rowIdx),
      value: priceValue,
      expr: priceValue === '' ? '' : String(priceValue),
      readOnly: isAutoImported || isDisabled,
      isRequired: isManualRowRequired,
      greaterThan: isManualRowRequired ? 0 : undefined,
      gridType: 'number',
      dbType: 'string',
      format: smallCurrencyFormat,
      columnLegend: alphabet[SOURCE_ROWS_TABLE_PRICE_INDEX],
      rowNumber,
    };

    // F: Weighting — always editable for non-disabled state, even for auto-imported rows
    const weightingValue = getNumericCellValue(row.weighting);
    cells[makeKey(SOURCE_ROWS_TABLE_WEIGHTING_INDEX, rowNumber)] = {
      key: makeKey(SOURCE_ROWS_TABLE_WEIGHTING_INDEX, rowNumber),
      alias: getSourceRowCellKey(SOURCE_ROW_WEIGHTING, row, rowIdx),
      value: weightingValue,
      expr: weightingValue === '' ? '' : String(weightingValue),
      readOnly: isDisabled,
      isRequired: isManualRowRequired,
      gridType: 'percentage',
      dbType: 'string',
      format: weightingPercentFormat,
      dbDecimalPlaces: 4,
      columnLegend: alphabet[SOURCE_ROWS_TABLE_WEIGHTING_INDEX],
      rowNumber,
    };
  });

  // Last row: totals via spreadsheet formulas so the workbook recalculates on edit.
  const totalsRowNumber = sourceRows.length + SOURCE_ROWS_TABLE_OFFSET;
  const dataRowNumbers = sourceRows.map((_, rowIdx) => rowIdx + SOURCE_ROWS_TABLE_OFFSET);
  const priceCells = dataRowNumbers
    .map(rowNumber => `${SOURCE_ROWS_TABLE_PRICE_COLUMN}${rowNumber}`)
    .join(',');
  const weightingCells = dataRowNumbers
    .map(rowNumber => `${SOURCE_ROWS_TABLE_WEIGHTING_COLUMN}${rowNumber}`)
    .join(',');
  const targetPriceExpr = dataRowNumbers.length
    ? `=SUMPRODUCT([${priceCells}],[${weightingCells}])`
    : '=0';
  const totalWeightingExpr = dataRowNumbers.length ? `=SUM(${weightingCells})` : '=0';

  // A: Target Price Per Share label
  cells[makeKey(SOURCE_ROWS_TABLE_TITLE_INDEX, totalsRowNumber)] = {
    ...BOLD_DIVISION_ROW_HEADER_CONFIG,
    key: makeKey(SOURCE_ROWS_TABLE_TITLE_INDEX, totalsRowNumber),
    alias: `${SOURCE_TOTALS_ROW}_label_target`,
    className: `${BOLD_DIVISION_ROW_HEADER_CONFIG.className} division-top-only division-bottom-only`,
    value: SOURCE_ROWS_TABLE_TARGET_PRICE_TITLE,
    expr: SOURCE_ROWS_TABLE_TARGET_PRICE_TITLE,
    readOnly: true,
    gridType: 'string',
    columnLegend: alphabet[SOURCE_ROWS_TABLE_TITLE_INDEX],
    rowNumber: totalsRowNumber,
  };

  // B, C, D: empty
  [SOURCE_ROWS_TABLE_DATE_INDEX, SOURCE_ROWS_TABLE_SOURCE_INDEX, SOURCE_ROWS_TABLE_SIZE_INDEX].forEach(colIdx => {
    const key = makeKey(colIdx, totalsRowNumber);
    cells[key] = {
      key,
      className: 'division-top-only division-bottom-only',
      value: '',
      expr: '',
      readOnly: true,
      gridType: 'string',
      columnLegend: alphabet[colIdx],
      rowNumber: totalsRowNumber,
    };
  });

  // E: weighted target price = SUMPRODUCT(price, weighting)
  cells[makeKey(SOURCE_ROWS_TABLE_PRICE_INDEX, totalsRowNumber)] = {
    key: makeKey(SOURCE_ROWS_TABLE_PRICE_INDEX, totalsRowNumber),
    alias: SOURCE_TOTALS_ROW_TARGET,
    customKey: SOURCE_TOTALS_ROW_TARGET,
    className: 'subtitle-bold division-top-only division-bottom-only',
    value: targetPriceExpr,
    expr: targetPriceExpr,
    readOnly: true,
    gridType: 'number',
    format: smallCurrencyFormat,
    columnLegend: alphabet[SOURCE_ROWS_TABLE_PRICE_INDEX],
    rowNumber: totalsRowNumber,
  };

  // F: Total Weighting = SUM(weightings)
  cells[makeKey(SOURCE_ROWS_TABLE_WEIGHTING_INDEX, totalsRowNumber)] = {
    key: makeKey(SOURCE_ROWS_TABLE_WEIGHTING_INDEX, totalsRowNumber),
    alias: `${SOURCE_TOTALS_ROW}_weighting`,
    customKey: `${SOURCE_TOTALS_ROW}_weighting`,
    className: 'division-top-only division-bottom-only',
    value: totalWeightingExpr,
    expr: totalWeightingExpr,
    readOnly: true,
    gridType: 'percentage',
    format: weightingPercentFormat,
    columnLegend: alphabet[SOURCE_ROWS_TABLE_WEIGHTING_INDEX],
    rowNumber: totalsRowNumber,
  };

  return cells;
};

export default customParser;
